import cx from "classnames";
import { useEffect, useId, useRef, useState } from "react";

import { useTranslation } from "@/core/i18n";
import { TextButton } from "atoms/text-button/TextButton";
import { FormattedString } from "utils/FormattedStrings";

export const PdpDescription = ({ description }: { description: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentId = useId();

  // Whether the line clamp hides anything depends on how many lines the text wraps to at the
  // column's current width, which only the browser's layout knows; re-measure as it resizes.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || expanded) return;

    const measure = () => setIsClamped(content.scrollHeight > content.clientHeight + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    measure();

    return () => observer.disconnect();
  }, [expanded, description]);

  return (
    <div>
      <div
        ref={contentRef}
        id={contentId}
        className={cx(
          // A line clamp cannot count lines inside a flex container, so the paragraphs'
          // flex column becomes a plain block with the same spacing.
          "break-words [&>div>*+*]:mt-3 [&>div]:block [&_p]:text-body2 [&_p]:leading-relaxed desk:[&_p]:text-body1",
          !expanded && "line-clamp-5 desk:line-clamp-[8]",
        )}
      >
        <FormattedString>{description}</FormattedString>
      </div>
      {(isClamped || expanded) && (
        <TextButton
          className="!px-0 !py-2 !text-body3 !font-medium underline underline-offset-4"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? t("common.showLess") : t("common.readMore")}
        </TextButton>
      )}
    </div>
  );
};
